import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import DashboardStatus from '../DashboardStatus/DashboardStatus';
import { 
  PlaySquareOutlined, 
  UserOutlined, 
  BookOutlined, 
  TeamOutlined,
  DollarOutlined,
  StarOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Table, Spin, message } from 'antd';
import { Line, Bar } from '@ant-design/charts';
import CreateClassModal from '../CreateClassModal/CreateClassModal';
import axios from 'axios';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const storedDeviceId = localStorage.getItem("deviceId");
        const storedUserIp = localStorage.getItem("userIp");
        
        // Get user's timezone (fallback to Africa/Dar_es_Salaam if unavailable)
        const userTimeZone = "Africa/Nairobi";
        console.log(userTimeZone)

        const response = await axios.post(
          'https://beyondschools.co.tz:8443/api/Admin/get-dashboard',
          {}, 
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              UserDevice: storedDeviceId,
              UserIPAddress: storedUserIp,
              time_zone: userTimeZone,
            },
          }
        );
        
        console.log('Dashboard data fetched:', response.data);
        setDashboardData(response.data.data); 
        setLoading(false);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        setLoading(false);
        message.error(`Failed to fetch dashboard data: ${errorMessage}`);
        
        if (err.response?.status === 401) {
          localStorage.removeItem("accessToken");
          window.location.href = '/login';
        }
      }
    };

    fetchDashboardData();
  }, []);

  // Status cards data - updated to match API response structure
  const statusData = [
    {
      icon: <PlaySquareOutlined />,
      title: 'Total Classes',
      value: dashboardData?.classes?.total ?? 0,
      lastWeekValue: dashboardData?.classes?.last_week ?? 0,
      iconColor: '#1890ff'
    },
    {
      icon: <UserOutlined />,
      title: 'Students',
      value: dashboardData?.students?.total ?? 0,
      lastWeekValue: dashboardData?.students?.last_week ?? 0,
      iconColor: '#722ed1'
    },
    {
      icon: <BookOutlined />,
      title: 'Subscribed Classes',
      value: dashboardData?.subscribed_classes?.total ?? 0,
      lastWeekValue: dashboardData?.subscribed_classes?.last_week ?? 0,
      iconColor: '#13c2c2'
    },
    {
      icon: <TeamOutlined />,
      title: 'Teachers',
      value: dashboardData?.teachers?.total ?? 0,
      lastWeekValue: dashboardData?.teachers?.last_week ?? 0,
      iconColor: '#fa8c16'
    },
  ];

  // Revenue Analytics - updated to match API response structure
  const revenueConfig = {
    data: dashboardData?.annual_revenue?.map(item => ({
      month: item.monthName,
      revenue: item.totalRevenue
    })) ?? Array(12).fill().map((_, i) => ({
      month: new Date(0, i).toLocaleString('default', { month: 'short' }),
      revenue: 0
    })),
    xField: 'month',
    yField: 'revenue',
    color: '#1890ff',
    yAxis: {
      label: {
        formatter: (v) => `${v} Ksh`,
      },
    },
    point: {
      size: 4,
      shape: 'circle',
    },
    smooth: true,
    areaStyle: {
      fill: 'l(270) 0:#ffffff 1:#1890ff',
      fillOpacity: 0.15,
    },
  };

  // Top Videos Table - updated to match API response structure
  const topVideosColumns = [
    {
      title: 'Video Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Subject',
      dataIndex: 'subect', // Note: API uses 'subect' not 'subject'
      key: 'subect',
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      sorter: (a, b) => a.views - b.views,
      render: (views) => views.toLocaleString(),
    },
    {
      title: 'Revenue (Tsh)',
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a, b) => a.revenue - b.revenue,
      render: (revenue) => revenue ? revenue.toLocaleString() : '0',
    },
  ];

  // Top Teachers Table - updated to match API response structure
  const topTeachersColumns = [
    {
      title: 'Teacher',
      dataIndex: 'teacher',
      key: 'teacher',
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Enrolled',
      dataIndex: 'enrolled',
      key: 'enrolled',
      sorter: (a, b) => a.enrolled - b.enrolled,
      render: (enrolled) => enrolled.toLocaleString(),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      sorter: (a, b) => a.rating - b.rating,
      render: (rating) => rating ? `${rating.toFixed(1)}/5.0` : 'N/A'
    },
  ];

  // Most Watched Classes by Level Chart - updated to match API response structure
  const classLevelConfig = {
    data: dashboardData?.most_watched_by_edu_class?.map(item => ({
      level: item.class_name,
      views: item.views
    })) ?? [{ level: 'No data', views: 0 }],
    xField: 'views',
    yField: 'level',
    colorField: 'level',
    color: ['#1890ff', '#722ed1', '#13c2c2', '#fa8c16', '#52c41a'],
    legend: {
      position: 'bottom',
    },
    yAxis: {
      label: {
        autoRotate: false,
      },
    },
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" tip="Loading dashboard data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>Error loading dashboard: {error}</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h3 className="dashboard-title">Welcome to the Admin Dashboard</h3>
          </div>
        </div>
      </div>
      
      <div className="dashboard-status-container">
        {statusData.map((status, index) => (
          <DashboardStatus
            key={`status-${index}`}
            icon={status.icon}
            title={status.title}
            value={status.value}
            lastWeekValue={status.lastWeekValue}
            iconColor={status.iconColor}
          />
        ))}
      </div>

      <div className="dashboard-content-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <DollarOutlined className="card-icon" style={{ color: '#1890ff' }} />
            <h3>Revenue Analytics (12 Months)</h3>
          </div>
          <div className="card-content">
            <Line {...revenueConfig} />
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <PlaySquareOutlined className="card-icon" style={{ color: '#722ed1' }} />
            <h3>Top Videos</h3>
          </div>
          <div className="card-content">
            <Table 
              columns={topVideosColumns}
              dataSource={dashboardData?.top_videos ?? []}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No video data available' }}
            />
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <TeamOutlined className="card-icon" style={{ color: '#fa8c16' }} />
            <h3>Top Teachers</h3>
          </div>
          <div className="card-content">
            <Table 
              columns={topTeachersColumns}
              dataSource={dashboardData?.top_teachers ?? []}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No teacher data available' }}
            />
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <BarChartOutlined className="card-icon" style={{ color: '#13c2c2' }} />
            <h3>Most Watched Classes by Level</h3>
          </div>
          <div className="card-content">
            <Bar {...classLevelConfig} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;